/* eslint-disable camelcase */

import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'

import unionBy from 'lodash/unionBy'

import * as Yup from 'yup'

const mapStateToProps = ({ loading }) => ({
  isLoading: loading['account/editAccount'],
  isRolesLoading: loading['account/getUserRoles'],
  isCustodiansLoading: loading['account/getCustodians'],
})

const inputConfig = {
  inputBaseProps: {
    labelSuffix: '',
    justifyContent: 'stretch',
    labelProps: {
      sx: {
        color: theme => theme.palette.btn_positive.main,
        width: 100,
        justifyContent: 'flex-end',
      },
    },
    childrenSx: { flex: 1 },
  },
  color: 'btn_positive',
}

const EditAccountDialog = ({
  dispatch,
  isLoading,
  isRolesLoading,
  isCustodiansLoading,
  onClose,
  onComplete,
  data,
  ...props
}) => {
  const rolePageRef = useRef(0)
  const custodianPageRef = useRef(0)
  const [rolesResults, setRolesResults] = useState({})
  const [custodiansResults, setCustodiansResults] = useState({})
  const form = useRef(null)
  const { t } = useTranslation()
  const [userData, setUserData] = useState()

  const { userId, status: originalStatus } = data || {}
  const isActive = originalStatus === 'active' || originalStatus === 'A'

  const { data: rolesResultsData = [], paging: rolesResultsPaging } = rolesResults
  const { data: custodiansResultsData = [], paging: custodiansResultsPaging } = custodiansResults

  const companyId = useSelector(state => state.auth.account?.companyId)

  const handleClose = () => {
    const { reset } = form.current
    setUserData()
    reset?.()
    onClose?.()
  }

  // const handleSubmit = () => {
  //   const { handleSubmit: formSubmit } = form.current

  //   formSubmit?.(values => {
  //     const { custodianId, email, phone, roleId, status } = values

  //     const role = rolesResultsData.find(r => r.value.toString() === roleId)
  //     const custodian = custodiansResultsData.find(c => c.value.toString() === custodianId)

  //     dispatch({
  //       type: 'account/editAccount',
  //       payload: {
  //         userId,
  //         email,
  //         phone,
  //         roleId,
  //         custodianId,
  //         roleName: role.label,
  //         custodian: custodian.label,
  //         status,
  //       },
  //       successCallback: () => {
  //         dispatch({
  //           type: 'app/notify',
  //           payload: { message: t('messages.account_updated', { email }), severity: 'success' },
  //         })

  //         onClose?.()
  //         onComplete?.()
  //       },
  //     })
  //   })?.()
  // }

  const handleSubmit = () => {
    const { handleSubmit: formSubmit } = form.current

    formSubmit?.(values => {
      const { custodianId, email, phone, roleId, status, employeeId } = values

      const role = rolesResultsData.find(r => r.value.toString() === roleId)
      const custodian = custodiansResultsData.find(c => c.value.toString() === custodianId)

      const roleName = role ? role.label : ''
      const custodianName = custodian ? custodian.label : ''

      dispatch({
        type: 'account/editAccount',
        payload: {
          userId,
          email,
          phone,
          roleId,
          custodianId,
          roleName,
          custodian: custodianName,
          status,
          employeeId,
        },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: { message: t('messages.account_updated', { email }), severity: 'success' },
          })

          onClose?.()
          onComplete?.()
        },
      })
    })?.()
  }

  const getRoles = params => {
    const { page = 0 } = params || {}
    if (!isRolesLoading) {
      rolePageRef.current = page

      dispatch({
        type: 'account/getUserRoles',
        payload: { pageIndex: rolePageRef.current, pageSize: 10 },
        successCallback: response => {
          const { roleId, roleName } = userData
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          let newData = responseData.map(d => ({ value: d.id, label: d.roleName }))
          newData = unionBy(newData, [{ value: roleId, label: roleName }], 'value')

          setRolesResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
            paging: responsePaging,
          }))

          if (form.current) {
            const { setValue } = form.current
            setValue?.('roleId', roleId)
          }
        },
      })
    }
  }

  const getCustodians = params => {
    const { page = 0 } = params || {}
    if (!isCustodiansLoading) {
      custodianPageRef.current = page

      dispatch({
        type: 'account/getCustodians',
        payload: { pageIndex: custodianPageRef.current, pageSize: 10, action: 'edit' },
        successCallback: response => {
          const { custodianId, custodian } = userData
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          let newData = responseData.map(d => ({ value: d.id, label: d.accountName }))

          // 檢查 newData 中是否已包含 custodianId
          const exists = newData.some(item => item.value === custodianId)

          if (exists) {
            newData = unionBy(newData, [{ value: custodianId, label: custodian }], 'value')
          }

          setCustodiansResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
            paging: responsePaging,
          }))

          if (form.current) {
            const { setValue } = form.current
            setValue?.('custodianId', custodianId)
          }
        },
      })
    }
  }

  const getUserData = () => {
    if (userId) {
      dispatch({
        type: 'account/getUserData',
        payload: { userId },
        successCallback: d => {
          const { phone, email, status, employeeId } = d
          setUserData(d)

          let newStatus = status

          switch (newStatus) {
            case 'A':
              newStatus = 'active'
              break
            case 'IA':
              newStatus = 'inactive'
              break
            default:
              break
          }

          if (form.current) {
            const { setValue } = form.current
            setValue?.('phone', phone)
            setValue?.('email', email)
            setValue?.('oriStatus', status)
            setValue?.('status', newStatus)
            setValue?.('employeeId', employeeId)
          }
        },
      })
    }
  }

  useEffect(() => {
    getUserData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  useEffect(() => {
    if (userData) {
      getRoles()
      getCustodians()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData])

  return (
    <Dialog {...props} maxWidth={'md'}>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: theme => theme.palette.primary.main,
        }}
      >
        {t('general.edit_account')}
      </DialogTitle>
      <DialogContent sx={{ width: 650, pr: 8 }}>
        <FormCreator
          ref={form}
          gridItemProps={{ md: 3 }}
          formItems={[
            {
              label: t('general.account_type'),
              name: 'roleId',
              disabled: !isActive,
              type: 'select',
              validator: Yup.string().required(t('validation.account_type_required')),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
                options: rolesResultsData,
                onLoadMore: () => {
                  if (rolesResultsPaging.hasNext) {
                    getRoles({ page: (rolePageRef.current += 1) })
                  }
                },
              },
            },
            {
              label: t('general.account_manager'),
              name: 'custodianId',
              disabled: !isActive,
              type: 'select',
              validator: Yup.string().when(['status', 'oriStatus'], {
                is: (status, oriStatus) => oriStatus === 'A' && status === 'active',
                then: Yup.string()
                  .required(t('validation.account_manager_required'))
                  .test('check-custodian', t('validation.account_manager_required'), value => {
                    const custodian = custodiansResultsData.find(c => c.value.toString() === value)
                    return !!custodian
                  }),
                otherwise: Yup.string().notRequired(),
              }),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
                options: custodiansResultsData,
                onLoadMore: () => {
                  if (custodiansResultsPaging.hasNext) {
                    getCustodians({ page: (custodianPageRef.current += 1) })
                  }
                },
              },
            },
            {
              label: t('general.phone'),
              name: 'phone',
              disabled: !isActive,
              validator: Yup.string()
                .required(t('validation.phone_required'))
                .matches(/^(09\d{8})?$/, t('validation.phone_ten_digits_start_zero_nine'))
                .test(
                  'check-starting-digits',
                  t('validation.phone_ten_digits_start_zero_nine'),
                  value => {
                    if (!value) return true
                    return value.startsWith('09')
                  },
                )
                .test(
                  'check-number-length',
                  t('validation.phone_ten_digits_start_zero_nine'),
                  value => {
                    if (!value) return true
                    return value.replace(/[^0-9]/g, '').length === 10
                  },
                ),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.email'),
              name: 'email',
              disabled: !isActive,
              validator: Yup.string()
                .required(t('validation.email_required'))
                .email(t('validation.email_format')),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
              },
            },
            companyId === 1
              ? {
                  label: t('general.employeeId'),
                  name: 'employeeId',
                  disabled: !isActive,
                  validator: Yup.string()
                    .nullable() //  允許 null
                    .when(['status', 'oriStatus'], (status, oriStatus, schema) => {
                      if (oriStatus === 'A' && status === 'active') {
                        return schema
                          .required(t('validation.employeeId_required'))
                          .matches(/^\d{7}$/, t('validation.employeeId_7_digits'))
                      }
                      return schema.notRequired()
                    }),
                  defaultValue: '',
                  inputConfig: {
                    ...inputConfig,
                    inputProps: {
                      maxLength: 7,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    },
                  },
                }
              : null,
            {
              label: t('general.status'),
              name: 'status',
              validator: Yup.string().required(t('validation.new_password_required')),
              defaultValue: '',
              type: 'radio',
              inputConfig: {
                ...inputConfig,
                options: [
                  { value: 'active', label: t('status.ACTIVE') },
                  { value: 'inactive', label: t('status.INACTIVE') },
                ],
              },
            },
          ].filter(Boolean)}
          submitComponent={() => null}
          resetComponent={() => null}
        />
      </DialogContent>
      <DialogActions sx={{ alignSelf: 'center' }}>
        <Button
          sx={{ minWidth: 150, marginRight: '30px' }}
          loading={isLoading}
          disabled={isLoading}
          onClick={handleClose}
        >
          {t('general.cancel')}
        </Button>
        <Button
          sx={{ minWidth: 150 }}
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            handleSubmit()
          }}
        >
          {t('general.complete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(EditAccountDialog)
